import { forwardRef } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { gr } from "../tokens";

// Shared field styling (also used by GrSelect and GrSearch): 44px, radius 10, line border,
// line-strong on hover, brand border + focus ring on focus, danger on error.
export const grFieldSx = {
  "& .MuiInputBase-root": {
    fontFamily: gr.font.family,
    fontSize: gr.font.size.md,
    color: gr.color.ink,
    background: gr.color.surface,
    borderRadius: gr.radius.control,
    transition: "box-shadow .15s ease",
  },
  "& .MuiInputBase-input": { height: "auto", lineHeight: "20px" },
  "& .MuiInputBase-input:not(textarea)": { padding: "12px 14px" },
  "& .MuiInputBase-inputAdornedStart": { paddingLeft: 0 },
  // Autocomplete (renderInput={(p) => <GrTextField {...p} />}) keeps the same 44px height.
  // "&&" beats MUI's own Autocomplete padding rules; these classes exist only inside an Autocomplete.
  "&& .MuiAutocomplete-inputRoot": { paddingTop: 0, paddingBottom: 0 },
  "&& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
    paddingTop: gr.space[3],
    paddingBottom: gr.space[3],
  },
  "& .MuiInputBase-input::placeholder": { color: gr.color.muted, opacity: 1 },
  // Browser autofill (saved email / password): keep our white field and ink text instead of
  // Chrome's blue fill.
  "& .MuiInputBase-input:-webkit-autofill": {
    WebkitBoxShadow: `0 0 0 100px ${gr.color.surface} inset`,
    WebkitTextFillColor: gr.color.ink,
    caretColor: gr.color.ink,
  },
  // ".MuiOutlinedInput-root" is repeated in these selectors on purpose: the global theme override
  // (src/theme/overrides/Input.js) styles the outline with the same specificity and loads later.
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.line,
    borderRadius: gr.radius.control,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.lineStrong,
  },
  "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: gr.shadow.focus },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.brand,
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline":
    { borderColor: gr.color.brand },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.danger,
  },
  "& .MuiOutlinedInput-root.Mui-disabled": { background: gr.color.bg },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.line,
  },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: gr.color.muted },
  "& .MuiInputLabel-root": {
    fontFamily: gr.font.family,
    fontSize: gr.font.size.md,
    color: gr.color.muted,
  },
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
    transform: "translate(14px, 12px) scale(1)",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: gr.color.brandDark },
  "& .MuiInputLabel-root.Mui-error": { color: gr.color.danger },
  "& .MuiInputAdornment-root": { color: gr.color.muted },
  "& .MuiInputAdornment-root svg": { width: 20, height: 20 },
  "& .MuiFormHelperText-root": {
    fontFamily: gr.font.family,
    fontSize: gr.font.size.xs,
    color: gr.color.muted,
    marginLeft: 0,
    marginRight: 0,
    marginTop: gr.space[1],
  },
  "& .MuiFormHelperText-root.Mui-error": { color: gr.color.danger },
};

// Look only (colours, border, radius, focus ring, labels) without changing field sizes or padding.
// Use it on a wrapper around existing MUI fields whose layout must stay as is (e.g. composite time inputs).
export const grFieldLookSx = {
  "& .MuiInputBase-root": {
    fontFamily: gr.font.family,
    color: gr.color.ink,
    borderRadius: gr.radius.control,
  },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.line,
    borderRadius: gr.radius.control,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.lineStrong,
  },
  "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: gr.shadow.focus },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.brand,
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: gr.color.line,
  },
  "& .MuiInputLabel-root": {
    fontFamily: gr.font.family,
    color: gr.color.muted,
  },
  "& .MuiInputLabel-root.Mui-focused": { color: gr.color.brandDark },
};

// Text input on MUI TextField (outlined). All TextField props pass through.
const GrTextField = forwardRef(function GrTextField(
  { fullWidth = true, sx, ...rest },
  ref
) {
  return (
    <TextField
      ref={ref}
      variant="outlined"
      fullWidth={fullWidth}
      sx={{ ...grFieldSx, ...sx }}
      {...rest}
    />
  );
});

GrTextField.propTypes = {
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default GrTextField;
